<template>
    <div class="module-component">
        <h2>{{ module.title }}</h2>
        <div v-html="module.descriptionMD" v-if="module.description && module.description.length > 0"></div>

        <div class="mb-4" v-if="userModules[module.id] && userModules[module.id].video">
            <mux-video-component :type="'exam-enrolment-module-admin-start'" :entity="userModules[module.id]" :track="false" />
        </div>

        <div v-if="!moduleStarted">
            <p v-if="isTimerEnabled && examTimerType.name === 'module'">
                Each question in this module is timed. You will be required to answer each question within the time limit.
            </p>
            <p>
                Once you answer the question you cannot go back to it.
            </p>
            <p>
                Your progress is saved when you complete a module.
            </p>

            <div class="text-center">
                <button class="btn btn-primary" @click="startModule">Start Module</button>
            </div>
        </div>
        <div v-else>

            <div class="row">
                <div class="col-md-4 order-0 order-md-1 text-md-right">
                    <div v-if="isTimerEnabled && examTimerType.name === 'module'" :class="['timer d-inline-block', { 'nearly-expired': timer <= 10 }]">
                        Time left: {{ formattedTimer }}
                    </div>
                </div>
                <div class="col-md-8 order-1 order-md-0">
                    <transition name="fade" mode="out-in">
                        <div v-if="currentQuestionIndex < module.questions.length" :key="currentQuestion.id" class="question">
                            <div v-if="isTimerEnabled && examTimerType.name === 'question'" :class="['timer', { 'nearly-expired': timer <= 10 }]">
                                Time left: {{ formattedTimer }}
                            </div>
                            <h5 class="mb-2 mt-4">Question {{ currentQuestionIndex + 1 }} of {{ module.questions.length }}</h5>
                            <question-component
                                :question="currentQuestion"
                                @answer-selected="handleAnswerSelected"
                            />
                            <button class="btn btn-primary my-3" @click="submitQuestion" :class="{ 'disabled' : isBusy }">
                                {{ isLastQuestion ? 'Complete Module' : 'Next Question' }}
                            </button>
                        </div>
                    </transition>

                    <p v-if="!areAllQuestionsAnswered" class="mb-0">
                        <small class="text-muted">
                            <i class="fas fa-exclamation-circle"></i>
                            Please answer all questions before completing the module.
                        </small>
                    </p>
                    <p>
                        <small class="text-muted">
                            Your progress will be saved when you complete the module.<br>
                            You will not be able to revisit this module once it is completed.
                        </small>
                    </p>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import QuestionComponent from './QuestionComponent.vue';

export default {
    components: {
        QuestionComponent,
    },
    props: {
        module: {
            type: Object,
            required: true,
        },
        userModules: {
            type: Array,
            required: false,
        },
        isBusy: {
            type: Boolean,
            default: false,
            required: true,
        },
        examTimerType: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            answers: {}, // Store user answers
            zeroMarkedQuestions: [], // Store questions that received 0 marks
            moduleStarted: false,
            currentQuestionIndex: 0, // Track the current question index
            timer: 0, // Timer for the current question or module
            timerInterval: null, // Interval for the timer
        };
    },
    computed: {
        currentQuestion() {
            return this.module.questions[this.currentQuestionIndex];
        },
        isLastQuestion() {
            return this.currentQuestionIndex === this.module.questions.length - 1;
        },
        areAllQuestionsAnswered() {
            const minQuestionsToBeAnswered = this.module.questions.filter(question => !question.is_optional).length;
            let answeredQuestions = 0;

            this.module.questions.forEach(question => {
                const answer = this.answers[question.id];
                if (answer !== undefined) { // Check if the question has any answer, including null
                    answeredQuestions++;
                }
            });

            return answeredQuestions >= minQuestionsToBeAnswered;
        },
        formattedTimer() {
            const hours = Math.floor(this.timer / 3600).toString().padStart(2, '0');
            const minutes = Math.floor((this.timer % 3600) / 60).toString().padStart(2, '0');
            const seconds = (this.timer % 60).toString().padStart(2, '0');
            return `${hours}:${minutes}:${seconds}`;
        },
        isTimerEnabled() {
            if (this.examTimerType.name === 'module' && this.module.is_timer_enabled) {
                return true;
            }
            if (this.examTimerType.name === 'question' && this.currentQuestion.is_timer_enabled) {
                return true;
            }
            return false;
        },
    },
    methods: {
        handleAnswerSelected(questionId, answer) {
            // Store the answer provided by the user
            this.$set(this.answers, questionId, answer);
        },
        submitQuestion() {
            if (this.answers[this.currentQuestion.id] === undefined && this.currentQuestion.is_optional === false) {
                alert('Please answer the question before proceeding. If you are adding a video to the question please click "Upload" before submitting.');
                return;
            }
            if (this.isLastQuestion) {
                this.submitModule();
            } else {
                this.currentQuestionIndex++;
                this.loadQuestion();
            }
        },
        submitModule() {
            if (!this.areAllQuestionsAnswered) {
                alert('Please answer all questions before completing the module. If you are adding a video to the question please click "Upload" before submitting.');
                return;
            }
            // Create a FormData object
            const formData = new FormData();

            // Append answers to formData
            for (const [questionId, answer] of Object.entries(this.answers)) {
                if (typeof answer === 'object' && answer !== null) {
                    // If the answer is an object, append each property to formData
                    for (const [key, value] of Object.entries(answer)) {
                        if (value instanceof File) {
                            // If the value is a File, append it to formData with the questionId and key as the name
                            formData.append(`${questionId}[${key}]`, value);
                        } else {
                            // Otherwise, append the value directly
                            formData.append(`${questionId}[${key}]`, value);
                        }
                    }
                } else {
                    formData.append(questionId, answer);
                }
            }

            // Append zero marked questions to formData
            formData.append('zero_marked_questions', JSON.stringify(this.zeroMarkedQuestions));

            // Emit an event with the formData
            this.$emit('complete-module', formData);
            // Clear answers after submission
            this.answers = {};
        },
        startModule() {
            this.moduleStarted = true;
            if (this.examTimerType.name === 'module' && this.isTimerEnabled) {
                this.startModuleTimer();
            }
            this.loadQuestion();
        },
        loadQuestion() {
            if (this.examTimerType.name === 'question') {
                clearInterval(this.timerInterval);
                if (this.currentQuestion.is_timer_enabled) {
                    this.startQuestionTimer();
                }
            }
        },
        startModuleTimer() {
            this.timer = this.module.timer_secs;
            this.timerInterval = setInterval(() => {
                if (this.timer > 0) {
                    this.timer--;
                } else {
                    clearInterval(this.timerInterval);
                    this.handleModuleTimeout();
                }
            }, 1000);
        },
        startQuestionTimer() {
            this.timer = this.currentQuestion.timer_secs;
            this.timerInterval = setInterval(() => {
                if (this.timer > 0) {
                    this.timer--;
                } else {
                    clearInterval(this.timerInterval);
                    this.handleTimeOut();
                }
            }, 1000);
        },
        handleModuleTimeout() {
            this.module.questions.forEach(question => {
                this.zeroMarkedQuestions.push(question.id); // Add all question IDs to zero marked questions
                this.$set(this.answers, question.id, null); // Set answer to null for timeout
            });
            this.submitModule();
        },
        handleTimeOut() {
            this.zeroMarkedQuestions.push(this.currentQuestion.id); // Add question ID to zero marked questions
            this.$set(this.answers, this.currentQuestion.id, null); // Set answer to null for timeout
            if (this.isLastQuestion) {
                this.submitModule();
            } else {
                this.currentQuestionIndex++;
                this.loadQuestion();
            }
        },
    },
    beforeDestroy() {
        clearInterval(this.timerInterval);
    },
};
</script>
